import type { PlannedMove } from '../../../types/preplanning';

type StopMove = Pick<PlannedMove, 'estimated_end' | 'dest_stop_minutes' | 'dest_stop_source' | 'dest_name' | 'fallback_dest_name'> & { legdrop?: string };

const clock = (date: Date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

const SOURCE: Record<string, string> = { location: 'location dwell', live: 'live, default', drop: 'drop, default' };

/** Where the planner holds the driver after a leg: at its destination, from arrival
 * until the stop ends. A card's own times are only the drive, so without this a
 * 90-minute live stop at a terminal read as a two-hour gap (Lee, 2026-10-28).
 * Null on a plan built before the board carried the stop. */
export function destinationStop(move: StopMove) {
  if (!move.estimated_end || !move.dest_stop_minutes) return null;
  const arrive = new Date(move.estimated_end);
  if (Number.isNaN(arrive.getTime())) return null;
  const leave = new Date(arrive.getTime() + move.dest_stop_minutes * 60_000);
  const where = move.dest_name && move.dest_name !== 'TBD' ? move.dest_name : move.fallback_dest_name || 'the destination';
  // Live holds the driver only when their next leg of the same dispatch follows;
  // at the end of a dispatch the planner uses the drop default (Lee, 2026-10-28).
  const endsLive = move.dest_stop_source === 'drop' && move.legdrop === 'N';
  const why = move.dest_stop_source === 'location'
    ? "the location's dwell time"
    : `${endsLive ? 'the drop default: the leg is marked live, but nothing follows it in its dispatch'
      : `the default for a ${move.dest_stop_source === 'drop' ? 'drop' : 'live'} stop`}. Set a dwell time on the location in Data Management → Locations to change it`;
  return {
    window: `${clock(arrive)} - ${clock(leave)}`,
    label: `${move.dest_stop_minutes}m ${SOURCE[move.dest_stop_source ?? 'live'] ?? ''}`.trim(),
    title: `Holds the driver at ${where} ${clock(arrive)}–${clock(leave)} (${move.dest_stop_minutes} min): ${why}.`,
  };
}
